import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PROJECT_STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", number: "", client: "", contractor: "", architect: "", site_manager: "", address: "", start_date: "", end_date: "", description: "", status: "planung", progress_mode: "fortschritt" };
const EMPTY_VIS = { number: true, client: true, contractor: true, architect: true, site_manager: true };

function OptField({ label, k, vis, setVis, children }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer select-none">
          <input type="checkbox" checked={vis[k]} onChange={(e) => setVis((s) => ({ ...s, [k]: e.target.checked }))} data-testid={`vis-${k}`} className="accent-primary h-3.5 w-3.5" />
          anzeigen
        </label>
      </div>
      {vis[k] ? <div className="mt-1.5">{children}</div> : <div className="mt-1.5 text-xs text-slate-400 italic h-10 flex items-center">wird nicht angezeigt</div>}
    </div>
  );
}

// Create (project = null) or edit an existing project's master data.
export default function ProjectFormDialog({ open, onOpenChange, project, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [vis, setVis] = useState(EMPTY_VIS);
  const [saving, setSaving] = useState(false);
  const edit = !!project;
  useEffect(() => {
    if (!open) return;
    const p = project || {};
    setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])));
    setVis({ ...EMPTY_VIS, ...(p.field_visibility || {}) });
  }, [open, project]);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, field_visibility: { ...(project?.field_visibility || {}), ...vis } };
      const { data } = edit ? await api.put(`/projects/${project.id}`, body) : await api.post("/projects", body);
      toast.success(edit ? "Projekt gespeichert" : "Projekt erstellt");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto scroll-thin" onClick={(e) => e.stopPropagation()} data-testid="project-form-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl">{edit ? "Projekt bearbeiten" : "Neues Projekt anlegen"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2"><Label>Projektname / Einrichtung *</Label><Input required value={form.name} onChange={on("name")} className="mt-1.5" data-testid="project-name-input" /></div>
          <OptField label="Projektnummer" k="number" vis={vis} setVis={setVis}><Input value={form.number} onChange={on("number")} data-testid="project-number-input" /></OptField>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={on("status")}>
              <SelectTrigger className="mt-1.5" data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <OptField label="Bauherr" k="client" vis={vis} setVis={setVis}><Input value={form.client} onChange={on("client")} data-testid="project-client-input" /></OptField>
          <OptField label="Auftragnehmer" k="contractor" vis={vis} setVis={setVis}><Input value={form.contractor} onChange={on("contractor")} data-testid="project-contractor-input" /></OptField>
          <OptField label="Architekt" k="architect" vis={vis} setVis={setVis}><Input value={form.architect} onChange={on("architect")} data-testid="project-architect-input" /></OptField>
          <OptField label="Bauleiter" k="site_manager" vis={vis} setVis={setVis}><Input value={form.site_manager} onChange={on("site_manager")} data-testid="project-site-manager-input" /></OptField>
          <div className="sm:col-span-2"><Label>Projektadresse</Label><Input value={form.address} onChange={on("address")} className="mt-1.5" data-testid="project-address-input" /></div>
          <div className="sm:col-span-2"><Label>Fortschrittsbalken zeigt</Label>
            <Select value={form.progress_mode} onValueChange={on("progress_mode")}>
              <SelectTrigger className="mt-1.5" data-testid="project-progress-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fortschritt">Fortschritt (erledigte Tickets)</SelectItem>
                <SelectItem value="maengel">Mängel-Erledigung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Startdatum</Label><Input type="date" value={form.start_date} onChange={on("start_date")} className="mt-1.5" data-testid="project-start-input" /></div>
          <div><Label>Geplantes Enddatum</Label><Input type="date" value={form.end_date} onChange={on("end_date")} className="mt-1.5" data-testid="project-end-input" /></div>
          <div className="sm:col-span-2"><Label>Beschreibung</Label><Textarea value={form.description} onChange={on("description")} className="mt-1.5" rows={3} data-testid="project-description-input" /></div>
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="save-project-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : edit ? "Änderungen speichern" : "Projekt erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
